// src/pages/users/UserDetails.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchUserDetails();
  }, [id]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getById(id);
      if (response.data.success) {
        setUser(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      setToast({ show: true, message: 'Failed to load user details', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

const handleExportData = async () => {
  try {
    const response = await usersAPI.exportUserData(id);
    
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `nisa-care-user-${id}-export-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    setToast({ show: true, message: 'User data exported successfully', type: 'success' });
  } catch (error) {
    console.error('Export error:', error);
    setToast({ show: true, message: error.response?.data?.message || 'Failed to export user data', type: 'error' });
  }
};

  if (loading) {
    return <LoadingSpinner message="Loading user details..." />;
  }

  if (!user) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-person-x fs-1 text-muted d-block mb-3"></i>
        <p className="text-muted">User not found</p>
        <button className="btn btn-primary" onClick={() => navigate('/users')}>
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* Header */}
      <div className="mb-4">
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/users')}>
          <i className="bi bi-arrow-left me-2"></i>
          Back to Users
        </button>

        <div className="stat-card">
          <div className="row">
            <div className="col-md-8">
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <h3 className="mb-1">{user.name}</h3>
                  <p className="text-muted mb-2">{user.email}</p>
                  <div className="d-flex gap-2">
                    <span className="badge badge-info">{user.madhab || 'Not Set'}</span>
                    {user.is_premium && <span className="badge badge-purple">Premium</span>}
                    {user.deleted_at ? (
                      <span className="badge badge-danger">Suspended</span>
                    ) : (
                      <span className="badge badge-success">Active</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="text-md-end">
                <small className="text-muted d-block">User ID</small>
                <p className="mb-2">{user.id}</p>
                <small className="text-muted d-block">Joined</small>
                <p className="mb-3">{new Date(user.created_at).toLocaleDateString()}</p>

                <button className="btn btn-outline-primary" onClick={handleExportData}>
                  <i className="bi bi-download me-2"></i>
                  Export Data (CSV)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-2">
          <div className="stat-card text-center">
            <small className="text-muted d-block mb-1">Daily Logs</small>
            <h4 className="mb-0">
              {user.stats?.total_daily_logs !== undefined && user.stats?.total_daily_logs !== null
                ? user.stats.total_daily_logs
                : <span className="text-muted">N/A</span>}
            </h4>
          </div>
        </div>
        <div className="col-md-2">
          <div className="stat-card text-center">
            <small className="text-muted d-block mb-1">Active Habits</small>
            <h4 className="mb-0">
              {user.stats?.active_habits !== undefined && user.stats?.active_habits !== null
                ? user.stats.active_habits
                : <span className="text-muted">N/A</span>}
            </h4>
          </div>
        </div>
        <div className="col-md-2">
          <div className="stat-card text-center">
            <small className="text-muted d-block mb-1">Notes</small>
            <h4 className="mb-0">
              {user.stats?.total_notes !== undefined && user.stats?.total_notes !== null
                ? user.stats.total_notes
                : <span className="text-muted">N/A</span>}
            </h4>
          </div>
        </div>
        <div className="col-md-2">
          <div className="stat-card text-center">
            <small className="text-muted d-block mb-1">Prayers</small>
            <h4 className="mb-0">
              {user.stats?.total_prayer_completions !== undefined && user.stats?.total_prayer_completions !== null
                ? user.stats.total_prayer_completions
                : <span className="text-muted">N/A</span>}
            </h4>
          </div>
        </div>
        <div className="col-md-2">
          <div className="stat-card text-center">
            <small className="text-muted d-block mb-1">Tasbih</small>
            <h4 className="mb-0">
              {user.stats?.total_tasbih_sessions !== undefined && user.stats?.total_tasbih_sessions !== null
                ? user.stats.total_tasbih_sessions
                : <span className="text-muted">N/A</span>}
            </h4>
          </div>
        </div>
        <div className="col-md-2">
          <div className="stat-card text-center">
            <small className="text-muted d-block mb-1">Bookmarks</small>
            <h4 className="mb-0">
              {user.stats?.total_bookmarks !== undefined && user.stats?.total_bookmarks !== null
                ? user.stats.total_bookmarks
                : <span className="text-muted">N/A</span>}
            </h4>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="stat-card">
        <ul className="nav nav-tabs border-bottom mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <i className="bi bi-grid me-2"></i>Overview
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <i className="bi bi-person me-2"></i>Profile
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'cycle' ? 'active' : ''}`}
              onClick={() => setActiveTab('cycle')}
            >
              <i className="bi bi-calendar-heart me-2"></i>Cycle & Logs
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'prayers' ? 'active' : ''}`}
              onClick={() => setActiveTab('prayers')}
            >
              <i className="bi bi-moon me-2"></i>Prayers & Qada
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'habits' ? 'active' : ''}`}
              onClick={() => setActiveTab('habits')}
            >
              <i className="bi bi-check-circle me-2"></i>Habits
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              <i className="bi bi-journal-text me-2"></i>Notes & Tasbih
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div>
            <div className="row g-4">
              {/* Prayer Streak */}
              {user.prayer_streak && (
                <div className="col-md-6">
                  <h5 className="mb-3"><i className="bi bi-fire text-danger me-2"></i>Prayer Streak</h5>
                  <div className="border rounded p-3">
                    <div className="row">
                      <div className="col-6">
                        <small className="text-muted">Current Streak</small>
                        <h4 className="mb-0 text-primary">{user.prayer_streak.current_streak} days</h4>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Longest Streak</small>
                        <h4 className="mb-0 text-success">{user.prayer_streak.longest_streak} days</h4>
                      </div>
                      <div className="col-12 mt-3">
                        <small className="text-muted">Total Prayers Completed</small>
                        <p className="mb-0">{user.prayer_streak.total_prayers_completed}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Qada Tracker */}
              {user.qada_tracker && (
                <div className="col-md-6">
                  <h5 className="mb-3"><i className="bi bi-bookmark-check me-2"></i>Qada Progress</h5>
                  <div className="border rounded p-3">
                    <div className="row g-2">
                      <div className="col-6">
                        <small className="text-muted">Fajr</small>
                        <p className="mb-0">{user.qada_tracker.fajr_completed}/{user.qada_tracker.fajr_owed}</p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Dhuhr</small>
                        <p className="mb-0">{user.qada_tracker.dhuhr_completed}/{user.qada_tracker.dhuhr_owed}</p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Asr</small>
                        <p className="mb-0">{user.qada_tracker.asr_completed}/{user.qada_tracker.asr_owed}</p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Maghrib</small>
                        <p className="mb-0">{user.qada_tracker.maghrib_completed}/{user.qada_tracker.maghrib_owed}</p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Isha</small>
                        <p className="mb-0">{user.qada_tracker.isha_completed}/{user.qada_tracker.isha_owed}</p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Fasting</small>
                        <p className="mb-0">{user.qada_tracker.fasting_completed}/{user.qada_tracker.fasting_owed}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Activity Summary */}
              <div className="col-12">
                <h5 className="mb-3"><i className="bi bi-activity me-2"></i>Recent Activity</h5>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-2">Last Daily Log</small>
                      {user.recent_logs && user.recent_logs.length > 0 ? (
                        <p className="mb-0">{new Date(user.recent_logs[0].log_date).toLocaleDateString()}</p>
                      ) : (
                        <p className="text-muted mb-0">No logs yet</p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-2">Last Tasbih Session</small>
                      {user.recent_tasbih && user.recent_tasbih.length > 0 ? (
                        <p className="mb-0">{new Date(user.recent_tasbih[0].session_date).toLocaleDateString()}</p>
                      ) : (
                        <p className="text-muted mb-0">No sessions yet</p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-2">Last Note</small>
                      {user.recent_notes && user.recent_notes.length > 0 ? (
                        <p className="mb-0">{new Date(user.recent_notes[0].created_at).toLocaleDateString()}</p>
                      ) : (
                        <p className="text-muted mb-0">No notes yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="row g-3">
            <div className="col-md-6">
              <label className="text-muted small">Name</label>
              <p className="mb-0">{user.name}</p>
            </div>
            <div className="col-md-6">
              <label className="text-muted small">Email</label>
              <p className="mb-0">{user.email}</p>
            </div>
            <div className="col-md-6">
              <label className="text-muted small">Phone</label>
              <p className="mb-0">{user.phone || 'Not provided'}</p>
            </div>
            <div className="col-md-6">
              <label className="text-muted small">Birthday</label>
              <p className="mb-0">
                {user.birthday ? new Date(user.birthday).toLocaleDateString() : 'Not provided'}
              </p>
            </div>
            <div className="col-md-6">
              <label className="text-muted small">City</label>
              <p className="mb-0">{user.city || 'Not provided'}</p>
            </div>
            <div className="col-md-6">
              <label className="text-muted small">Country</label>
              <p className="mb-0">{user.country || 'Not provided'}</p>
            </div>
            <div className="col-md-6">
              <label className="text-muted small">Madhab</label>
              <p className="mb-0">{user.madhab || 'Not set'}</p>
            </div>
            <div className="col-md-6">
              <label className="text-muted small">Account Type</label>
              <p className="mb-0">{user.is_premium ? 'Premium' : 'Free'}</p>
            </div>
            <div className="col-12">
              <label className="text-muted small">Bio</label>
              <p className="mb-0">{user.bio || 'No bio provided'}</p>
            </div>
            <div className="col-12">
              <label className="text-muted small">Goals</label>
              <div className="d-flex gap-2 flex-wrap">
                {user.goals && user.goals.length > 0 ? (
                  user.goals.map((goal, index) => (
                    <span key={index} className="badge badge-purple">{goal}</span>
                  ))
                ) : (
                  <p className="text-muted mb-0">No goals set</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cycle' && (
          <div>
            {/* Cycle Settings */}
            {user.cycle_settings ? (
              <div className="mb-4">
                <h5 className="mb-3"><i className="bi bi-calendar-heart me-2"></i>Cycle Settings</h5>
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Cycle Length</small>
                      <h4 className="mb-0">{user.cycle_settings.cycle_length || 'N/A'} days</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Period Duration</small>
                      <h4 className="mb-0">{user.cycle_settings.period_duration || 'N/A'} days</h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Last Period Start</small>
                      <h4 className="mb-0" style={{ fontSize: '0.9rem' }}>
                        {user.cycle_settings.last_period_start_date
                          ? new Date(user.cycle_settings.last_period_start_date).toLocaleDateString()
                          : 'Not set'}
                      </h4>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Currently On Period</small>
                      <h4 className="mb-0">
                        {user.cycle_settings.is_currently_on_period ? (
                          <span className="badge badge-danger" style={{ fontSize: '0.9rem' }}>
                            <i className="bi bi-circle-fill me-1"></i>Yes
                          </span>
                        ) : (
                          <span className="badge badge-success" style={{ fontSize: '0.9rem' }}>
                            <i className="bi bi-check-circle-fill me-1"></i>No
                          </span>
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                No cycle settings configured yet
              </div>
            )}

            {/* Daily Logs */}
            <div className="mb-4">
              <h5 className="mb-3"><i className="bi bi-journal-medical me-2"></i>Recent Daily Logs ({user.recent_daily_logs?.length || 0})</h5>
              {user.recent_daily_logs && user.recent_daily_logs.length > 0 ? (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Mood</th>
                        <th>Flow</th>
                        <th>Symptoms</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.recent_daily_logs.map((log) => (
                        <tr key={log.id}>
                          <td>
                            <small className="fw-semibold">
                              {new Date(log.log_date).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            {log.mood ? (
                              <span className="badge badge-info">{log.mood}</span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {log.flow ? (
                              <span className={`badge ${log.flow === 'Heavy' ? 'badge-danger' :
                                log.flow === 'Medium' ? 'badge-warning' :
                                  'badge-success'
                                }`}>
                                {log.flow}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {log.symptoms && Array.isArray(log.symptoms) && log.symptoms.length > 0
                                ? log.symptoms.join(', ')
                                : 'None'}
                            </small>
                          </td>
                          <td>
                            <small className="text-muted">{log.notes || '-'}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No daily logs yet</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'prayers' && (
          <div>
            {/* Prayer Streak */}
            {user.prayer_streak && (
              <div className="mb-4">
                <h5 className="mb-3">Prayer Streak</h5>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted">Current Streak</small>
                      <h3 className="mb-0 text-primary">{user.prayer_streak.current_streak} days</h3>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted">Longest Streak</small>
                      <h3 className="mb-0 text-success">{user.prayer_streak.longest_streak} days</h3>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted">Total Completed</small>
                      <h3 className="mb-0 text-info">{user.prayer_streak.total_prayers_completed}</h3>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Qada Tracker */}
            {user.qada_tracker && (
              <div className="mb-4">
                <h5 className="mb-3">Qada Tracker</h5>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Prayer</th>
                        <th>Owed</th>
                        <th>Completed</th>
                        <th>Remaining</th>
                        <th>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Fajr</td>
                        <td>{user.qada_tracker.fajr_owed}</td>
                        <td>{user.qada_tracker.fajr_completed}</td>
                        <td>{user.qada_tracker.fajr_owed - user.qada_tracker.fajr_completed}</td>
                        <td>
                          {user.qada_tracker.fajr_owed > 0 && (
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${(user.qada_tracker.fajr_completed / user.qada_tracker.fajr_owed) * 100}%`
                                }}
                              ></div>
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Dhuhr</td>
                        <td>{user.qada_tracker.dhuhr_owed}</td>
                        <td>{user.qada_tracker.dhuhr_completed}</td>
                        <td>{user.qada_tracker.dhuhr_owed - user.qada_tracker.dhuhr_completed}</td>
                        <td>
                          {user.qada_tracker.dhuhr_owed > 0 && (
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${(user.qada_tracker.dhuhr_completed / user.qada_tracker.dhuhr_owed) * 100}%`
                                }}
                              ></div>
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Asr</td>
                        <td>{user.qada_tracker.asr_owed}</td>
                        <td>{user.qada_tracker.asr_completed}</td>
                        <td>{user.qada_tracker.asr_owed - user.qada_tracker.asr_completed}</td>
                        <td>
                          {user.qada_tracker.asr_owed > 0 && (
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${(user.qada_tracker.asr_completed / user.qada_tracker.asr_owed) * 100}%`
                                }}
                              ></div>
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Maghrib</td>
                        <td>{user.qada_tracker.maghrib_owed}</td>
                        <td>{user.qada_tracker.maghrib_completed}</td>
                        <td>{user.qada_tracker.maghrib_owed - user.qada_tracker.maghrib_completed}</td>
                        <td>
                          {user.qada_tracker.maghrib_owed > 0 && (
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${(user.qada_tracker.maghrib_completed / user.qada_tracker.maghrib_owed) * 100}%`
                                }}
                              ></div>
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Isha</td>
                        <td>{user.qada_tracker.isha_owed}</td>
                        <td>{user.qada_tracker.isha_completed}</td>
                        <td>{user.qada_tracker.isha_owed - user.qada_tracker.isha_completed}</td>
                        <td>
                          {user.qada_tracker.isha_owed > 0 && (
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar bg-success"
                                style={{
                                  width: `${(user.qada_tracker.isha_completed / user.qada_tracker.isha_owed) * 100}%`
                                }}
                              ></div>
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr className="table-light">
                        <td><strong>Fasting</strong></td>
                        <td><strong>{user.qada_tracker.fasting_owed}</strong></td>
                        <td><strong>{user.qada_tracker.fasting_completed}</strong></td>
                        <td><strong>{user.qada_tracker.fasting_owed - user.qada_tracker.fasting_completed}</strong></td>
                        <td>
                          {user.qada_tracker.fasting_owed > 0 && (
                            <div className="progress" style={{ height: '8px' }}>
                              <div
                                className="progress-bar bg-primary"
                                style={{
                                  width: `${(user.qada_tracker.fasting_completed / user.qada_tracker.fasting_owed) * 100}%`
                                }}
                              ></div>
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Qada History */}
            {user.qada_history && user.qada_history.length > 0 && (
              <div>
                <h5 className="mb-3">Recent Qada Completions</h5>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Prayer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.qada_history.map((item, index) => (
                        <tr key={index}>
                          <td><small>{new Date(item.completed_at).toLocaleDateString()}</small></td>
                          <td><span className="badge badge-success">{item.completion_type}</span></td>
                          <td>{item.prayer_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'habits' && (
          <div>
            <h5 className="mb-3">Active Habits ({user.habits ? user.habits.length : 0})</h5>
            {user.habits && user.habits.length > 0 ? (
              <div className="row g-3">
                {user.habits.map((habit) => (
                  <div key={habit.id} className="col-md-6">
                    <div className="border rounded p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">{habit.name}</h6>
                        <span className="badge badge-purple">{habit.category}</span>
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <small className="text-muted">Current Streak</small>
                          <p className="mb-0">{habit.current_streak} days</p>
                        </div>
                        <div className="col-6">
                          <small className="text-muted">Longest Streak</small>
                          <p className="mb-0">{habit.longest_streak} days</p>
                        </div>
                        <div className="col-6">
                          <small className="text-muted">Total</small>
                          <p className="mb-0">{habit.total_completions}</p>
                        </div>
                        <div className="col-6">
                          <small className="text-muted">Frequency</small>
                          <p className="mb-0">{habit.frequency}</p>
                        </div>
                      </div>
                      {habit.recent_completions && habit.recent_completions.length > 0 && (
                        <div>
                          <small className="text-muted">Recent completions:</small>
                          <div className="d-flex gap-1 flex-wrap mt-1">
                            {habit.recent_completions.map((comp, idx) => (
                              <span key={idx} className="badge badge-success badge-sm">
                                {new Date(comp.completion_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted">No habits created yet</p>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div>
            {/* Notes */}
            <div className="mb-4">
              <h5 className="mb-3"><i className="bi bi-journal-text me-2"></i>Recent Notes ({user.recent_notes?.length || 0})</h5>
              {user.recent_notes && user.recent_notes.length > 0 ? (
                <div className="row g-3">
                  {user.recent_notes.map((note) => (
                    <div key={note.id} className="col-md-6">
                      <div className="border rounded p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0">{note.title}</h6>
                          <span className="badge badge-purple">{note.category}</span>
                        </div>
                        <p className="mb-2 text-muted small" style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {note.content}
                        </p>
                        <small className="text-muted">
                          {new Date(note.created_at).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No notes yet</p>
              )}
            </div>

            {/* Tasbih Sessions */}
            <div className="mb-4">
              <h5 className="mb-3"><i className="bi bi-infinity me-2"></i>Recent Tasbih Sessions ({user.recent_tasbih?.length || 0})</h5>
              {user.recent_tasbih && user.recent_tasbih.length > 0 ? (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Dhikr Text</th>
                        <th>Count</th>
                        <th>Target</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.recent_tasbih.map((session) => (
                        <tr key={session.id}>
                          <td>
                            <small className="fw-semibold">
                              {new Date(session.session_date).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <small className="text-muted">
                              {session.dhikr_text || 'General Dhikr'}
                            </small>
                          </td>
                          <td>
                            <span className="badge badge-success">{session.count}</span>
                          </td>
                          <td>
                            <small className="text-muted">{session.target_count}</small>
                          </td>
                          <td>
                            <small className="text-muted">
                              {session.duration_seconds
                                ? `${Math.floor(session.duration_seconds / 60)}m ${session.duration_seconds % 60}s`
                                : '-'}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No tasbih sessions yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetails;